/**
 * chartScales.js
 * ─────────────────────────────────────────────────────────────────────────────
 * All x-axis scale and timezone logic for MarketPulze charts.
 *
 * Exports:
 *   getNthSunday(year, month, nth)        — DST boundary calculator
 *   toET(date)                            — UTC Date → US Eastern Time Date
 *   etFmt(date, tabIndex)                 — format a Date per active timeframe tab
 *   createDateIndexScale(linear, dates, map) — gap-free trading-day scale
 *
 * Why this is isolated:
 *   Every part of the chart that touches time (x-axis ticks, crosshair pills,
 *   top bar, tooltip, session lines) imports from here. Keeping it separate means:
 *   - DST bugs are fixed in exactly one place
 *   - New timeframe formats only need one file change
 *   - Unit-testable with known input/output (no React, no D3 side effects)
 *
 * Dependencies: d3 (utcFormat only — no DOM usage here)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import * as d3 from "d3";

// ── DST helpers ───────────────────────────────────────────────────────────────

/**
 * getNthSunday(year, month, nth)
 * Returns the UTC Date of the Nth Sunday in a given month.
 *
 * Usage:
 *   getNthSunday(2024, 2,  2) → 2nd Sunday of March 2024  (EDT starts)
 *   getNthSunday(2024, 10, 1) → 1st Sunday of November 2024 (EST starts)
 *
 * @param {number} year  — full year, e.g. 2024
 * @param {number} month — 0-indexed (0=Jan, 2=Mar, 10=Nov)
 * @param {number} nth   — which Sunday (1=first, 2=second)
 * @returns {Date} UTC Date at midnight on that Sunday
 */
export function getNthSunday(year, month, nth) {
  const d = new Date(Date.UTC(year, month, 1));
  // Advance to first Sunday of the month
  d.setUTCDate(1 + ((7 - d.getUTCDay()) % 7));
  // Advance to the nth Sunday
  d.setUTCDate(d.getUTCDate() + (nth - 1) * 7);
  return d;
}

// ── Eastern Time conversion ────────────────────────────────────────────────────

/**
 * toET(date)
 * Converts any UTC Date to its US Eastern Time equivalent.
 * Automatically applies EDT (UTC-4) or EST (UTC-5) based on the year's DST dates.
 *
 * Why: All US equity market data is timestamped in UTC. Displaying times in the
 * user's local timezone is meaningless for trading — "09:30 ET" is the session
 * open regardless of whether the user is in London, Sydney or New York.
 *
 * @param {Date} date — any UTC Date
 * @returns {Date}    — new Date shifted to ET (use .getUTCHours() to read ET hour)
 */
export function toET(date) {
  const year     = date.getUTCFullYear();
  const edtStart = getNthSunday(year, 2,  2); // 2nd Sunday March  → spring forward
  const edtEnd   = getNthSunday(year, 10, 1); // 1st Sunday November → fall back
  const offsetMs = (date >= edtStart && date < edtEnd) ? -4 * 3600000 : -5 * 3600000;
  return new Date(date.getTime() + offsetMs);
}

// ── Per-tab x-axis label formatter ────────────────────────────────────────────

/**
 * etFmt(date, tabIndex)
 * Formats a Date for display on the x-axis or in tooltips, using ET timezone.
 * The format changes based on which timeframe tab is active.
 *
 * Tab → Resolution → Format → Example
 *   0  1D  5m/15m   "HH:MM ET"    "09:30 ET"
 *   1  1W  1hr      "ddd HH:MM"   "Mon 09:30"
 *   2  1M  daily    "Mmm DD"      "Apr 07"
 *   3  3M  daily    "Mmm DD"      "Feb 10"
 *   4  1Y  daily    "Mmm DD"      "Jul 01"
 *   5  5Y  weekly   "Mmm 'YY"     "Jan '24"
 *
 * @param {Date}   date     — candle date (UTC-based from data feed)
 * @param {number} tabIndex — active TABS index (0=1D, 1=1W, 2=1M, 3=3M, 4=1Y, 5=5Y)
 * @returns {string}
 */
export function etFmt(date, tabIndex) {
  const et = toET(date);
  if (tabIndex === 0) return d3.utcFormat("%H:%M")(et) + " ET"; // 1D — show session time
  if (tabIndex === 1) return d3.utcFormat("%a %H:%M")(et);       // 1W — day + time
  if (tabIndex <= 3)  return d3.utcFormat("%b %d")(et);          // 1M, 3M — date only
  if (tabIndex === 4) return d3.utcFormat("%b %d")(et);          // 1Y — date only
  return d3.utcFormat("%b '%y")(et);                             // 5Y — month + short year
}

// ── Gap-free index-based x-scale ──────────────────────────────────────────────

/**
 * createDateIndexScale(linear, tradingDates, dateToIndex)
 *
 * Builds a custom D3-compatible scale that maps Date objects to pixel positions
 * using an index-based linear scale underneath. This eliminates weekend and
 * holiday gaps that appear when using d3.scaleTime() (which maps calendar time).
 *
 * The wrapper is a drop-in replacement for scaleTime:
 *   scale(date)      → px (accepts Date or timestamp)
 *   scale.invert(px) → Date (returns nearest trading day)
 *   scale.range()    → [0, width]
 *   scale.domain()   → [firstDate, lastDate]
 *   scale.copy()     → new wrapper with copied linear scale
 *   scale._linear    → the inner d3.scaleLinear (exposed for zoom.rescaleX)
 *
 * Why exposed _linear:
 *   d3-zoom's rescaleX() only works on continuous scales (scaleLinear, scaleTime).
 *   We pass ._linear to rescaleX(), then re-wrap the result. This preserves zoom
 *   behaviour while keeping the trading-day-only mapping.
 *
 * Fast path: exact timestamp match via Map lookup O(1)
 * Slow path: binary search for nearest trading day — used by indicators whose
 *            timestamps may not align exactly with candle timestamps.
 *
 * @param {d3.ScaleLinear} linear       — d3.scaleLinear().domain([0, n-1]).range([0, width])
 * @param {Date[]}         tradingDates — array of candle Date objects in order
 * @param {Map<number,number>} dateToIndex — timestamp → index lookup map
 * @returns {Function} scale wrapper with D3-compatible API
 */
export function createDateIndexScale(linear, tradingDates, dateToIndex) {
  function scale(date) {
    const ts = date instanceof Date ? date.getTime() : +date;
    // Fast path — exact timestamp match (all real candle dates)
    if (dateToIndex.has(ts)) return linear(dateToIndex.get(ts));
    // Slow path — binary search for nearest trading day
    let lo = 0, hi = tradingDates.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (tradingDates[mid].getTime() <= ts) lo = mid; else hi = mid;
    }
    const nearIdx =
      Math.abs((tradingDates[lo]?.getTime() || 0) - ts) <=
      Math.abs((tradingDates[hi]?.getTime() || 0) - ts)
        ? lo : hi;
    return linear(nearIdx);
  }

  // .invert(px) → Date — used by crosshair bisect and drawing click handlers
  scale.invert = px => {
    const idx = Math.round(linear.invert(px));
    return tradingDates[Math.max(0, Math.min(tradingDates.length - 1, idx))];
  };

  // Forward .range() and .domain() — resize and zoom handlers use these
  scale.range  = (...args) => {
    if (!args.length) return linear.range();
    linear.range(...args);
    return scale;
  };
  scale.domain = (...args) => {
    if (!args.length) return linear.domain();
    linear.domain(...args);
    return scale;
  };

  // .copy() — used internally by D3 axis and zoom
  scale.copy = () => createDateIndexScale(linear.copy(), tradingDates, dateToIndex);

  // ._linear — exposed so zoom handler can call rescaleX on the inner linear scale
  scale._linear = linear;

  return scale;
}
